/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import orderBy from 'lodash/orderBy';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// UI lib dependencies
import {
  Stack,
  Group,
  Menu,
  Skeleton,
  Text,
  ActionIcon,
  Box,
  Badge,
} from '@mantine/core';

// Context
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';

// Store
import useAccount from '@features/Account/store/useAccount';

// API & Types
import { WorkspaceStatusType, useGetMeQuery } from '@app-types/blackroom-api';
import { WorkspaceSelectorProps } from './types';

// Assets
import { IconChevronDown as IconSelect, IconCheck } from '@tabler/icons-react';

// Style
import { palette } from '@ui-kit/colors';
import * as classes from './styles/index.css';
import * as menuStylesAPI from './styles/menuStylesAPI.css';
import { FONT_WEIGHT } from '@ui-kit/fonts';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function WorkspaceSelector({
  onSelectWorkspace,
  ...props
}: WorkspaceSelectorProps) {
  /* ********************************** HOOKS ********************************* */

  // Context
  const workspace = useWorkspace();

  // Store
  const { account: user } = useAccount();

  // API
  const { data: meData } = useGetMeQuery();

  // Routing
  const navigate = useNavigate();

  // Localization
  const { t } = useTranslation();

  /* ***************************** LOCAL VARIABLES **************************** */

  const unsortedUserWorkspaces = (meData?.me.workspaceUsers ?? []).map(
    (wu) => wu.workspace,
  );
  const userWorkspaces = orderBy(
    orderBy(unsortedUserWorkspaces, 'name', 'asc'),
    'status',
    'asc',
  );

  /* ******************************** CALLBACKS ******************************* */

  function onChangeWorkspace(selectedWorkspaceId: string) {
    if (selectedWorkspaceId !== workspace?.id) {
      const targetWorkspace = userWorkspaces.find(
        (w) => w.id === selectedWorkspaceId,
      );
      if (targetWorkspace) {
        navigate(`/workspace/${targetWorkspace.id}/resources/my`);
      }
      if (onSelectWorkspace) {
        onSelectWorkspace(selectedWorkspaceId);
      }
    }
  }

  /* ******************************** RENDERING ******************************* */

  if (!workspace || !user) {
    return (
      <>
        <Skeleton height={8} radius="xl" />
        <Skeleton height={8} mt={6} radius="xl" />
        <Skeleton height={8} mt={6} width="70%" radius="xl" />
      </>
    );
  }

  return (
    <>
      <Menu classNames={{ ...menuStylesAPI }} width={260} withinPortal>
        <Menu.Target>
          <Group className={classes.workspaceSelectorButton} role="button">
            <Stack
              className={classes.workspaceName}
              gap={0}
              justify="center"
              align="start"
            >
              <Text
                c="blackroomWhite"
                size="sm"
                fw={FONT_WEIGHT.bolder}
                lineClamp={1}
              >
                {workspace.name}
              </Text>
              <Badge
                variant="filled"
                size="xs"
                color={
                  workspace.status === WorkspaceStatusType.Live
                    ? 'blackroomGreen'
                    : 'blackroomOrange'
                }
              >
                {t(`Workspace:shared.sandbox.status.${workspace.status}`)}
              </Badge>
            </Stack>

            <ActionIcon
              className={classes.workspaceArrow}
              variant="transparent"
              size={24}
              aria-label={t('Workspace:shared.selectWorkspace')}
            >
              <IconSelect size={15} color="white" />
            </ActionIcon>
          </Group>
        </Menu.Target>

        <Menu.Dropdown>
          {userWorkspaces.map((w) => (
            <Menu.Item
              key={w.id}
              leftSection={
                w.id === workspace?.id ? (
                  <IconCheck size={20} color={palette.blackroomBlue} />
                ) : (
                  <Box w={20} />
                )
              }
              rightSection={
                <Badge
                  variant="filled"
                  size="xs"
                  color={
                    w.status === WorkspaceStatusType.Live
                      ? 'blackroomGreen'
                      : 'blackroomOrange'
                  }
                  fw={FONT_WEIGHT.light}
                >
                  {t(`Workspace:shared.sandbox.status.${w.status}`)}
                </Badge>
              }
              onClick={() => onChangeWorkspace(w.id)}
            >
              <Box fw={w.id === workspace?.id ? FONT_WEIGHT.bold : undefined}>
                {w.name}
              </Box>
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    </>
  );
}

export default WorkspaceSelector;
